import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ReactComponent as PersonDash } from "../../../../assets/icons/flowbite_user-remove-outline.svg";
import { ReactComponent as GreenIcon } from "../../../../assets/icons/Greeen.svg";
import { ReactComponent as SearchIcon } from "../../../../assets/icons/Fill 1.svg";
import { debounce } from "lodash";
import { ReactComponent as ArrowIcon } from "../../../../assets/icons/Arrow-down.svg";
import { Form, Button } from "react-bootstrap";
import {
  Box,
  Typography,
  Select,
  Table,
  TableBody,
  IconButton,
  Menu,
  MenuItem,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { KeyboardArrowDown } from "@mui/icons-material";
import Pagination from "../../PaginationComp/Paginations";
import axios from "axios";
import DatabaseApi from "../../../../services/database_api";
import { CircularProgress } from "@mui/material";

interface Driver {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  password?: string;
  account_type?: string;
  company_name?: string;
  vehicle_type?: string;
  vehicle_color?: string;
  license_plate_number?: string;
  license_document?: string | null;
  towing_license?: string | null;
  insurance_document?: string | null;
  upload_company_document?: string | null;
  account_status?: string;
  created_at: string;
  updated_at: string;
  subscriptionStatus?: string;
  subscriptionStartDate?: string;
  subscriptionEndDate?: string;
}

interface ApiResponse {
  data: any[];
  current_page: number;
  total_page: number;
  total_data: number;
  status: string;
}

const DriverComponent: React.FC = () => {
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [activationStatus, setActivationStatus] = useState(() => {
    return location.state?.returnToStatus || "Activate";
  });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [sortOrder, setSortOrder] = useState("");
  const navigate = useNavigate();
  const StatusIcon = activationStatus === "Activate" ? PersonDash : GreenIcon;

  useEffect(() => {
    if (location.state?.returnToStatus) {
      navigate(location.pathname, { replace: true });
    }
  });

  const searchTimeout = useRef<NodeJS.Timeout>();
  const latestRequestId = useRef(0);

  const fetchDrivers = useCallback(
    async (
      pageNumber: number,
      searchQuery: string,
      type: "old" | "new" | "" = ""
    ) => {
      const requestId = ++latestRequestId.current;
      const token = localStorage.getItem("authToken");

      try {
        setLoading(true);
        const response = await axios.get<ApiResponse>(
          DatabaseApi.getDriversUrl(
            pageNumber,
            activationStatus,
            type,
            searchQuery
          ),
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (requestId === latestRequestId.current) {
          if (response.data && response.data.status === "true") {
            const driverData = Array.isArray(response.data.data)
              ? response.data.data
              : [];
            setDrivers(driverData);
            setTotalPages(response.data.total_page);
            setTotalItems(response.data.total_data);
            setCurrentPage(response.data.current_page);
          } else {
            resetPaginationState();
          }
        }
      } catch (error) {
        if (requestId === latestRequestId.current) {
          resetPaginationState();
        }
      } finally {
        if (requestId === latestRequestId.current) {
          setLoading(false);
        }
      }
    },
    [activationStatus]
  );

  // Helper function to reset pagination state
  const resetPaginationState = () => {
    setDrivers([]);
    setTotalPages(1);
    setTotalItems(0);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage !== currentPage && newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
      fetchDrivers(
        newPage,
        searchQuery,
        sortOrder === "Earliest" ? "old" : sortOrder === "Latest" ? "new" : ""
      );
    } else {
    }
  };

  const handleSearch = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchQuery(value);

      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }

      searchTimeout.current = setTimeout(() => {
        setCurrentPage(1);
        const sortType =
          sortOrder === "Earliest"
            ? "old"
            : sortOrder === "Latest"
            ? "new"
            : "";
        fetchDrivers(1, value, sortType);
      }, 500);
    },
    [sortOrder, fetchDrivers]
  );

  useEffect(() => {
    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, []);

  // Update useEffect to include sortOrder in dependencies
  useEffect(() => {
    const sortType = sortOrder === "Earliest" ? "old" : sortOrder === "Latest" ? "new" : "";
    fetchDrivers(currentPage, searchQuery, sortType);
  }, [activationStatus, sortOrder, fetchDrivers]);

  // handle Menu
  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleSortSelection = (order: string) => {
    setSortOrder(order);
    handleCloseMenu();

    const sortType = order === "Earliest" ? "old" : "new";
    fetchDrivers(currentPage, searchQuery, sortType);
  };

  const handleRowClick = (driver: Driver) => {
    let path;

    if (driver.account_status === "Pending") {
      path = `/admin/drivers/pending-driver/${driver.id}`;
    } else if (driver.account_status === "Deactivate") {
      path = `/admin/drivers/rejected-driver/${driver.id}`;
    } else {
      path = `/admin/drivers/driver-details/${driver.id}`;
    }
    navigate(path, { state: { driver } });
  };

  const handleIconClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation(); // Prevent event from propagating to the Select component
    setIsOpen(!isOpen);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress size={40} />
      </Box>
    );
  }

  return (
    <div
      className="p-6 bg-white min-h-screen "
      style={{ maxWidth: "90%", marginLeft: "240px", marginTop: "45px" }}
    >
      <Box sx={{ p: 3 }}>
        <div className="flex justify-between items-center mb-4">
          <Typography
            variant="h5"
            gutterBottom
            color="#2C3E50"
            style={{ fontFamily: "Montserrat", fontWeight: "600" }}
          >
            Drivers
          </Typography>
          <div className="flex items-center gap-4">
            <Select
              open={isOpen}
              onClose={() => setIsOpen(false)}
              onOpen={() => setIsOpen(true)}
              value={activationStatus}
              onChange={(e) => setActivationStatus(e.target.value as string)}
              IconComponent={() => (
                <div
                  onClick={handleIconClick}
                  style={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    marginRight: "8px",
                  }}
                >
                  <ArrowIcon
                    style={{
                      width: "2rem",
                      height: "2rem",
                      marginRight: "8px",
                      backgroundImage: `url(${ArrowIcon})`,
                      backgroundSize: "cover",
                      color: "#FDA214",
                      transition: "transform 0.3s ease",
                      transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                    }}
                  />
                </div>
              )}
              sx={{
                width: 200,
                height: "3rem",
                borderRadius: "10px",
                "& fieldset": {
                  borderRadius: "10px", // Remove the border
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#FDA214", // Custom border color on focus
                  boxShadow: "0 0 0 2px #FDA214", // Custom box shadow on focus
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#FDA214", // Optional: set border color on hover
                },
              }}
            >
              <MenuItem value="Activate" style={{ fontFamily: "Montserrat" }}>
                Activated
              </MenuItem>
              <MenuItem value="Pending" style={{ fontFamily: "Montserrat" }}>
                Pending Review
              </MenuItem>
              <MenuItem value="Deactivate" style={{ fontFamily: "Montserrat" }}>
                Rejected List
              </MenuItem>
            </Select>

            <div className="relative">
              <SearchIcon
                className="absolute left-4 top-1/2 transform -translate-y-1/2"
                style={{
                  width: "20px",
                  height: "20px",
                  color: "#FDA214",
                  fontFamily: "Montserrat",
                }}
              />

              <Form.Control
                type="text"
                placeholder="Search Users by Name / Email / Phone No."
                className="pl-10 pr-6 py-2 w-112 rounded-full border border-gray-300"
                value={searchQuery}
                onChange={handleSearch}
                style={{
                  width: "30rem",
                  fontFamily: "Montserrat",
                  height: "3.5rem",
                  lineHeight: "3rem",
                  borderRadius: "20px",

                  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                }}
              />
              <style>
                {`
                 .relative input::placeholder {
                 color: #B4BDC4; // Placeholder color
                  }
                `}
              </style>
            </div>
          </div>
        </div>

        <TableContainer component={Paper} elevation={0}>
          <Table>
            <TableHead sx={{ backgroundColor: "#F0F3FA" }}>
              <TableRow>
                <TableCell
                  sx={{ fontWeight: "bold", py: 1, fontFamily: "Montserrat" }}
                >
                  Driver Name
                </TableCell>
                <TableCell
                  sx={{ fontWeight: "bold", py: 1, fontFamily: "Montserrat" }}
                >
                  Phone no.
                </TableCell>
                <TableCell
                  sx={{ fontWeight: "bold", py: 1, fontFamily: "Montserrat" }}
                >
                  Email
                </TableCell>
                <TableCell
                  sx={{
                    padding: "30px",
                    fontWeight: "bold",
                    py: 1,
                    fontFamily: "Montserrat",
                  }}
                >
                  Date Joined
                  <IconButton onClick={handleOpenMenu} size="small">
                    <KeyboardArrowDown />
                  </IconButton>
                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleCloseMenu}
                  >
                    <MenuItem
                      onClick={() => handleSortSelection("Earliest")}
                      style={{ fontFamily: "Montserrat" }}
                    >
                      Earliest
                    </MenuItem>
                    <MenuItem
                      onClick={() => handleSortSelection("Latest")}
                      style={{ fontFamily: "Montserrat" }}
                    >
                      Latest
                    </MenuItem>
                  </Menu>
                </TableCell>
                <TableCell
                  sx={{ fontWeight: "bold", py: 1, fontFamily: "Montserrat" }}
                >
                  Action
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {drivers.map((driver) => (
                <TableRow
                  key={driver.id}
                  onClick={() => handleRowClick(driver)}
                  style={{
                    cursor: "pointer",
                    fontFamily: "Montserrat",
                    backgroundColor:
                      hoveredRow === driver.id ? "#f0f0f0" : "transparent",
                  }}
                  onMouseEnter={() => setHoveredRow(driver.id)}
                  onMouseLeave={() => setHoveredRow(null)}
                >
                  <TableCell
                    sx={{ py: 0.5, fontFamily: "Montserrat" }}
                  >{`${driver.first_name} ${driver.last_name}`}</TableCell>
                  <TableCell
                    sx={{
                      color: "#93310D",
                      fontWeight: "bold",
                      textDecoration: "underline",
                      textDecorationColor: "#93310D",
                      py: 0.5,
                      fontFamily: "Montserrat",
                    }}
                  >
                    {driver.phone}
                  </TableCell>
                  <TableCell sx={{ py: 0.5, fontFamily: "Montserrat" }}>
                    {driver.email}
                  </TableCell>
                  <TableCell sx={{ py: 0.5, fontFamily: "Montserrat" }}>
                    {new Date(driver.created_at)
                      .toLocaleString("en-GB", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      })
                      .replace(/\//g, "-")
                      .toUpperCase()}
                  </TableCell>

                  <TableCell sx={{ py: 0.5, fontFamily: "Montserrat" }}>
                    <Button
                      variant="link"
                      className="text-gray-500 hover:text-gray-700 p-1"
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <StatusIcon
                        className="ml-2"
                        style={{
                          width: "30px",
                          height: "30px",
                          backgroundSize: "cover",
                          fontFamily: "Montserrat",
                        }}
                      />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <div
          className="d-flex justify-content-center  align-items-center mt-4 flex-wrap p-3 rounded-lg"
          style={{ height: "100px" }}
        >
          {totalPages > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          )}
        </div>
      </Box>
    </div>
  );
};

export default DriverComponent;
